import { useSuspenseQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';

import {
  ModuleHeader,
  UpsellBanner,
  WarningMessageBox as Error,
} from '@xing-com/crate-entity-pages-common';
import {
  ContactsGrid,
  createContactsCards,
  EntityPagesContactsListDocument,
} from '@xing-com/crate-entity-pages-contacts';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

const ContactsDetailInner: FC = () => {
  const { pageContext } = usePageContext();
  const { data, refetch } = useSuspenseQuery(EntityPagesContactsListDocument, {
    variables: {
      id: pageContext.pageSlug,
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const upsellActive = pageContext.upsellActive;
  const contactsNodes = data?.entityPageContactUsers?.collection ?? [];

  if (contactsNodes.length === 0) {
    return (
      <>
        <ModuleHeader
          editTestId="EDIT_BUTTON_ON_CONTACTS"
          headlineCopyKey="EP_WHO_TO_CONTACT"
          editTo={`${pageContext.basePath}/${pageContext.pageSlug}/edit/contacts`}
          small
        />
        <div data-testid={'errorContainer'}>
          <Error
            headerText="EP_ERROR_HEADER"
            bodyText="EP_ERROR_BODY"
            buttonText="EP_ERROR_RELOAD_CTA"
            onClick={refetch}
          />
        </div>
      </>
    );
  }

  const contactsCards = createContactsCards(contactsNodes);

  return (
    <>
      {upsellActive && (
        <UpsellBanner
          headerCopyKey="EP_UPSELL_CONTACT_HEADER"
          bodyCopyKey="EP_UPSELL_CONTACT_BODY"
          moduleName="contactsmodule"
        />
      )}

      <ModuleHeader
        editTestId="EDIT_BUTTON_ON_CONTACTS"
        headlineCopyKey="EP_WHO_TO_CONTACT"
        editTo={`${pageContext.basePath}/${pageContext.pageSlug}/edit/contacts`}
        small
      />

      <ContactsGrid
        contactCards={contactsCards}
        messageEntryPoint="entity_pages_sub_contact_message"
        trackingModuleType="Subpage"
      />
    </>
  );
};

const LoadingFallback: FC = () => {
  const { pageContext } = usePageContext();

  return (
    <>
      <ModuleHeader
        editTestId="EDIT_BUTTON_ON_CONTACTS"
        headlineCopyKey="EP_WHO_TO_CONTACT"
        editTo={`${pageContext.basePath}/${pageContext.pageSlug}/edit/contacts`}
        small
      />
      <ContactsGrid.Skeleton />
    </>
  );
};

export const ContactsDetail: FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ContactsDetailInner />
    </Suspense>
  );
};
